/* Warehouse Bold — the "Show all N" toggle on collection sub-categories.
 *
 * Deliberately tiny and deliberately not required: every category link is in
 * the server-rendered HTML. With this script blocked the overflow links stay
 * hidden, but the first 8 (sidebar) or 3 (chips) are still there and still
 * work, and so does everything else on the page.
 *
 * ⚠ Overflow items use the `hidden` ATTRIBUTE, not a clipped container. A link
 * clipped by `overflow: hidden` is still focusable, so a keyboard user tabs
 * into a category they cannot see. Toggling the attribute moves them in and out
 * of the tab order along with the paint.
 */
(() => {
  const bind = (btn) => {
    if (btn.dataset.wbsBound) return;
    btn.dataset.wbsBound = 'true';

    const nav = btn.closest('.wb-subcats');
    if (!nav) return;
    const items = nav.querySelectorAll('[data-wbs-over]');
    if (!items.length) {
      // Nothing to reveal — the count changed under us (a filter narrowed the
      // list, say). A button that does nothing is worse than no button.
      btn.hidden = true;
      return;
    }

    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      items.forEach((el) => { el.hidden = open; });
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      btn.textContent = open ? btn.dataset.more : btn.dataset.less;
      /* Collapsing from below the fold would otherwise leave the shopper
         staring at wherever the shortened list happens to end. */
      if (open) nav.scrollIntoView({ block: 'nearest' });
    });
  };

  const init = () => document.querySelectorAll('[data-wbs-more]').forEach(bind);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  /* The facet column is re-rendered on every filter apply (facets.js), and the
     theme editor re-renders whole sections. The nav itself sits OUTSIDE the
     rewritten container, so this is belt-and-braces rather than load-bearing. */
  document.addEventListener('shopify:section:load', init);

  /* ---------------------------------------------------------------------------
     Toolbar sticky point: measure the meta row so phones can pin from the
     Filter row down (Owen, 2026-08-16).
     --------------------------------------------------------------------------- */

  /* wb-collection.css shifts --sticky-offset up by this height on phones so the
     "N products / Compare" row lifts out of view and only Filter + the view
     switcher stay pinned. It has to be MEASURED: the product-count string is
     rewritten by facets.js on every filter apply and can wrap to a second line
     on a narrow phone, which would leave a hardcoded value pinning the wrong
     part of the bar.
     ⚠ Measured as a delta between two rects, so it is correct whether or not the
     bar is currently stuck — both boxes move together. */
  const measureToolbar = () => {
    document.querySelectorAll('.wb-toolbar-sticky').forEach((wrap) => {
      // The first control of the second row: the Filter button, or the view
      // switcher when filtering is turned off for the collection.
      const row2 = wrap.querySelector('.facet-drawer-wrapper') || wrap.querySelector('layout-switcher');
      if (!row2) return;
      const gap = Math.round(row2.getBoundingClientRect().top - wrap.getBoundingClientRect().top);
      // A negative or absurd reading means the rows are side by side (tablet
      // and up) or the bar is mid-relayout; leave the property alone so the CSS
      // falls back to 0px rather than pinning somewhere arbitrary.
      if (gap > 0 && gap < 400) wrap.style.setProperty('--wb-toolbar-meta-h', gap + 'px');
      else wrap.style.removeProperty('--wb-toolbar-meta-h');
    });
  };

  const scheduleMeasure = (() => {
    let raf = null;
    return () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { raf = null; measureToolbar(); syncPinned(); });
    };
  })();

  /* Measure once synchronously, THEN again on the next frame.
     ⚠ The synchronous pass is not redundant: requestAnimationFrame never fires
     while a document is hidden, so a page rendered in a background tab (or in a
     headless/preview pane) would otherwise pin the toolbar at the wrong place
     until something else nudged it. The rAF pass then refines the value once
     layout has settled. */
  /* Is the bar actually pinned right now? Owen, 2026-08-16: a shadow sitting
     under the bar while it is still in the flow "looks weird" — it should only
     appear once the bar is floating over the grid.
     ⚠ Compared against the element's own resolved `top` rather than 0, because
     the pin line is not the viewport edge: it is negative on phones (the bar is
     lifted so the meta row hides) and grows by the header height once the
     mobile header collapses. Reading the resolved value each time means this
     stays correct in every header state without duplicating the base theme's formulas.
     ⚠ Bound to `scroll` directly rather than an rAF/IntersectionObserver
     throttle: neither of those runs in a hidden document, and scroll already
     fires at most once per frame. */
  const syncPinned = () => {
    document.querySelectorAll('.wb-toolbar-sticky').forEach((wrap) => {
      const pin = parseFloat(getComputedStyle(wrap).top);
      if (Number.isNaN(pin)) return;
      wrap.classList.toggle('is-pinned', wrap.getBoundingClientRect().top <= pin + 1);
    });
  };

  let lastY = -1;
  const onScroll = () => {
    const y = window.scrollY;
    if (Math.abs(y - lastY) < 2) return;
    lastY = y;
    syncPinned();
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  measureToolbar();
  scheduleMeasure();
  syncPinned();
  // A tab loaded in the background lays out differently; re-measure when shown.
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleMeasure(); });
  window.addEventListener('resize', scheduleMeasure);
  window.addEventListener('orientationchange', scheduleMeasure);
  document.addEventListener('shopify:section:load', scheduleMeasure);
  // Barlow arriving re-flows the row: measure again once it has.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleMeasure);
  // facets.js rewrites the product count in place; a longer string can rewrap it.
  const count = document.getElementById('ProductCount');
  if (count && window.MutationObserver) {
    new MutationObserver(scheduleMeasure).observe(count, { childList: true, subtree: true, characterData: true });
  }
})();
